import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const ScientificProjectForm = ({
    isOpen, onClose, formData, setFormData, isEditing,
    users = [], countries = [], topicLevels = [],
    members = [], setMembers, toast, fetchData
}) => {

    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPendingFiles([]);
            setDeletedFileIds([]);
        }
    }, [isOpen]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return {
                label: displayLabel,
                value: u.Id,
                searchKey: searchStr,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const countryOptions = useMemo(() => {
        return countries.map(c => ({
            label: c.Name,
            value: c.Name
        }));
    }, [countries]);

    const existingFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            const parsed = JSON.parse(formData.AttachmentUrl);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }, [formData.AttachmentUrl]);

    const isReadOnlyForm = useMemo(() => {
        const currentUserStr = localStorage.getItem('user');
        const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
        const currentUserId = currentUserInfo ? Number(currentUserInfo.Id) : 0;

        const isLockedStatus = formData.Status === 'Đã gửi duyệt' || formData.Status === 'Đã duyệt';

        const isNotOwner = formData.UserId && Number(formData.UserId) !== currentUserId;

        return isLockedStatus || isNotOwner;
    }, [formData.Status, formData.UserId]);

    useEffect(() => {
        if (isOpen && members.length > 0) {
            const updatedMembers = members.map(m => {
                if (m.userId || m.UserId) {
                    const user = users.find(u => u.Id === parseInt(m.userId || m.UserId || m.Id));
                    if (user) {
                        return {
                            ...m,
                            userId: user.Id,
                            UserId: user.Id,
                            fullName: `${user.LastName || ''} ${user.FirstName || ''}`.trim(),
                            FullName: `${user.LastName || ''} ${user.FirstName || ''}`.trim(),
                            department: user.DepartmentName || m.department || m.DepartmentName || ''
                        };
                    }
                }
                return m;
            });

            if (JSON.stringify(members) !== JSON.stringify(updatedMembers)) {
                setMembers(updatedMembers);
            }
        }
    }, [isOpen, users, members, setMembers]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (isReadOnlyForm) return;
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDateChange = (name, dateObj) => {
        if (isReadOnlyForm) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString } });
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnlyForm) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isAdmin = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/scientificproject?userId=${userId}&isAdmin=${isAdmin}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.StartDate ? formData.StartDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.Title);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.StartDate ? item.StartDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (formData.ProjectLevel && item.ProjectLevel === formData.ProjectLevel)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện đề tài rất giống đã tồn tại: "${item.Title}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const handleFileDescChange = (index, desc) => {
        if (isReadOnlyForm) return;
        const updated = [...existingFiles];
        updated[index].description = desc;
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnlyForm) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const allSelectedFiles = Array.from(e.target.files);
        if (allSelectedFiles.length === 0) return;

        const validFiles = allSelectedFiles.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const removeExistingFile = (fileId) => {
        if (isReadOnlyForm) return;
        setDeletedFileIds(prev => [...prev, fileId]);
        const updated = existingFiles.filter(f => f.fileId !== fileId && f.url !== fileId && f.link !== fileId && f.data !== fileId);
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const removePendingFile = (index) => {
        if (isReadOnlyForm) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadprojectevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL) {
            fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
            if (typeof fileUrl === 'string') {
                fileUrl = fileUrl.trim();
                if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                    fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
                }
            }
        }

        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleMemberSelect = (index, userId) => {
        if (isReadOnlyForm) return;
        const selectedUser = users.find(u => u.Id === parseInt(userId));
        const newMembers = [...members];
        if (selectedUser) {
            newMembers[index] = {
                ...newMembers[index],
                userId: selectedUser.Id,
                UserId: selectedUser.Id,
                fullName: `${selectedUser.LastName} ${selectedUser.FirstName}`.trim(),
                FullName: `${selectedUser.LastName} ${selectedUser.FirstName}`.trim(),
                department: selectedUser.DepartmentName || ''
            };
        }
        setMembers(newMembers);
    };

    const handleRoleChange = (index, newRole) => {
        if (isReadOnlyForm) return;
        const newMembers = [...members];
        newMembers[index].role = newRole;
        newMembers[index].Role = newRole;
        setMembers(newMembers);
    };

    const handleRemoveMember = (index) => {
        if (isReadOnlyForm) return;
        const newMembers = members.filter((_, i) => i !== index);
        setMembers(newMembers);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnlyForm) return;

        if (existingFiles.length === 0 && pendingFiles.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Thiếu thông tin',
                detail: 'Vui lòng đính kèm ít nhất một tệp tin minh chứng!',
                life: 4000
            });
            return;
        }

        const currentUserStr = localStorage.getItem('user');
        const currentUser = currentUserStr ? JSON.parse(currentUserStr) : {};
        if (!currentUser || !currentUser.Id) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Vui lòng đăng nhập lại!', life: 3000 });
            return;
        }

        if (members.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng thêm ít nhất một thành viên!', life: 3000 });
        }

        const userRoleName = (currentUser.RoleName || '').toLowerCase();
        const isManager = currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
            ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true);

        let finalUserId = currentUser.Id;

        if (isManager) {
            const leader = members.find(m => m.role === "Chủ nhiệm" || m.Role === "Chủ nhiệm");
            if (leader) {
                const leaderId = leader.userId || leader.UserId || leader.Id || leader.id;
                if (leaderId) {
                    finalUserId = leaderId;
                } else {
                    toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Thành viên "Chủ nhiệm" chưa được chọn người dùng! Vui lòng chọn lại.', life: 5000 });
                    return;
                }
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Vui lòng chỉ định ít nhất một "Chủ nhiệm" đề tài!', life: 5000 });
                return;
            }
        }

        setIsSubmittingForm(true);

        let cleanEvidence = [];
        try {
            const currentFiles = JSON.parse(formData.AttachmentUrl || '[]');
            cleanEvidence = currentFiles.filter(f => !f.fileId);
        } catch (e) { }

        const payload = {
            ...formData,
            UserId: parseInt(finalUserId),
            MembersJSON: JSON.stringify(members),
            AttachmentUrl: JSON.stringify(cleanEvidence),
            Id: isEditing ? formData.Id : 0
        };

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificproject`, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
                return;
            }

            let result;
            try {
                result = await response.json();
            } catch (e) {
                result = { status: 'error', message: 'Lỗi máy chủ không xác định!' };
            }

            if (response.ok && result.status !== 'error') {
                const finalProjectId = result.projectId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(fId => {
                        if (typeof fId === 'number') {
                            return fetch(`${process.env.REACT_APP_API_BASE_URL}/deleteprojectevidence?fileId=${fId}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                            });
                        }
                        return Promise.resolve();
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(pf => {
                        const fd = new FormData();
                        fd.append('projectId', finalProjectId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);
                        return fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadprojectevidence`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
                            body: fd
                        });
                    }));
                }

                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: isManager && parseInt(finalUserId) !== parseInt(currentUser.Id) ? 'Đã kê khai hộ thành công!' : 'Lưu dữ liệu và tệp đính kèm thành công!',
                    life: 3000
                });
                if (typeof fetchData === 'function') fetchData(true);
                onClose();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: result.message || 'Lưu thất bại!', life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsSubmittingForm(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>

                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnlyForm ? "Xem chi tiết Đề tài khoa học" : (isEditing ? "Cập nhật Đề tài khoa học" : "Thêm mới Đề tài khoa học")}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="projectForm" onSubmit={handleFormSubmit}>
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tên đề tài *</label>
                            <input
                                type="text"
                                name="Title"
                                className="form-input"
                                value={formData.Title || ''}
                                onChange={handleChange}
                                onBlur={handleTitleBlur}
                                disabled={isReadOnlyForm}
                                required
                            />
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mã số *</label>
                                <input type="text" name="ProjectCode" className="form-input" value={formData.ProjectCode || ''} onChange={handleChange} disabled={isReadOnlyForm} required />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Cấp đề tài *</label>
                                <select name="ProjectLevel" className="form-input" value={formData.ProjectLevel || ''} onChange={handleChange} disabled={isReadOnlyForm} required>
                                    <option value="">Chọn cấp đề tài</option>
                                    {topicLevels.map(l => <option key={l.Id} value={l.Name}>{l.Name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tóm tắt</label>
                            <textarea name="Summary" className="form-input" rows="3" value={formData.Summary || ''} onChange={handleChange} disabled={isReadOnlyForm} placeholder="Cần cập nhật" style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày bắt đầu *</label>
                                <Calendar
                                    id="StartDate"
                                    name="StartDate"
                                    value={(formData.StartDate && !isNaN(new Date(formData.StartDate).getTime())) ? new Date(formData.StartDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('StartDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnlyForm}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày kết thúc *</label>
                                <Calendar
                                    id="EndDate"
                                    name="EndDate"
                                    value={(formData.EndDate && !isNaN(new Date(formData.EndDate).getTime())) ? new Date(formData.EndDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('EndDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnlyForm}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày nghiệm thu</label>
                                <Calendar
                                    id="AcceptanceDate"
                                    name="AcceptanceDate"
                                    value={(formData.AcceptanceDate && !isNaN(new Date(formData.AcceptanceDate).getTime())) ? new Date(formData.AcceptanceDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('AcceptanceDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnlyForm}
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Xếp loại</label>
                                <select name="Classification" className="form-input" value={formData.Classification || ''} onChange={handleChange} disabled={isReadOnlyForm}>
                                    <option value="">Chọn xếp loại</option>
                                    <option value="Xuất sắc">Xuất sắc</option>
                                    <option value="Tốt">Tốt</option>
                                    <option value="Khá">Khá</option>
                                    <option value="Đạt">Đạt</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Quốc gia</label>
                                <SearchableFocusDropdown
                                    value={formData.Country}
                                    options={countryOptions}
                                    onChange={(e) => handleChange({ target: { name: 'Country', value: e.value } })}
                                    placeholder="Chọn quốc gia"
                                    className="form-input"
                                    panelUniqueClass="custom-dropdown-panel-country"
                                    disabled={isReadOnlyForm}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 'bold', margin: 0 }}>Các thành viên tham gia *</label>
                                <button
                                    type="button"
                                    className="btn-add-new"
                                    style={{
                                        margin: 0,
                                        padding: '5px 12px',
                                        fontSize: '12px',
                                        backgroundColor: isReadOnlyForm ? '#bdc3c7' : '#3498db',
                                        cursor: isReadOnlyForm ? 'not-allowed' : 'pointer',
                                        border: 'none', borderRadius: '4px', color: '#fff'
                                    }}
                                    onClick={() => setMembers([...members, { userId: '', UserId: '', fullName: '', FullName: '', role: 'Thành viên', isExternal: false }])}
                                    disabled={isReadOnlyForm}
                                >
                                    <i className="fa-solid fa-user-plus"></i> Chọn người dùng
                                </button>
                            </div>

                            <div style={{ overflowX: 'auto', border: '1px solid #eee' }}>
                                <table className="custom-table" style={{ width: '100%', minWidth: '950px', margin: 0 }}>
                                    <thead style={{ backgroundColor: '#f9f9f9' }}>
                                        <tr>
                                            <th width="5%" style={{ textAlign: 'center' }}>STT</th>
                                            <th width="32%">Họ tên</th>
                                            <th width="23%">Đơn vị công tác</th>
                                            <th width="25%">Vai trò</th>
                                            <th width="10%" style={{ textAlign: 'center' }}>Ngoài trường</th>
                                            <th width="5%" style={{ textAlign: 'center' }}>Xóa</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.length === 0 ? (
                                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Chưa có thành viên nào</td></tr>
                                        ) : members.map((m, i) => {
                                            const isExt = m.isExternal || (!m.userId && !m.UserId && (m.fullName || m.FullName));

                                            return (
                                                <tr key={i}>
                                                    <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                    <td>
                                                        {isExt ? (
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder="Nhập tên người ngoài trường..."
                                                                value={m.fullName || m.FullName || ''}
                                                                onChange={(e) => {
                                                                    const updated = [...members];
                                                                    updated[i].fullName = e.target.value;
                                                                    updated[i].FullName = e.target.value;
                                                                    setMembers(updated);
                                                                }}
                                                                disabled={isReadOnlyForm}
                                                                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                                            />
                                                        ) : (
                                                            <SearchableFocusDropdown
                                                                value={m.userId || m.UserId}
                                                                options={userOptions}
                                                                onChange={(e) => handleMemberSelect(i, e.value)}
                                                                placeholder="Chọn thành viên"
                                                                panelUniqueClass={`custom-dropdown-panel-member-${i}`}
                                                                disabled={isReadOnlyForm}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '13px', color: '#555' }}>{m.department || m.DepartmentName || (isExt ? 'Ngoài trường' : '---')}</span>
                                                    </td>
                                                    <td>
                                                        <select
                                                            className="form-input"
                                                            value={m.role || m.Role || ''}
                                                            onChange={(e) => handleRoleChange(i, e.target.value)}
                                                            disabled={isReadOnlyForm}
                                                            style={{ width: '100%', minWidth: '140px', cursor: isReadOnlyForm ? 'not-allowed' : 'pointer' }}
                                                            required
                                                        >
                                                            <option value="" disabled>Chọn vai trò</option>
                                                            <option value="Chủ nhiệm">Chủ nhiệm</option>
                                                            <option value="Thành viên chính">Thành viên chính</option>
                                                            <option value="Thành viên">Thành viên</option>
                                                            <option value="Thư ký khoa học">Thư ký khoa học</option>
                                                        </select>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isExt}
                                                            onChange={(e) => {
                                                                const updated = [...members];
                                                                updated[i].isExternal = e.target.checked;
                                                                if (e.target.checked) {
                                                                    updated[i].userId = '';
                                                                    updated[i].UserId = '';
                                                                } else {
                                                                    updated[i].fullName = '';
                                                                    updated[i].FullName = '';
                                                                }
                                                                setMembers(updated);
                                                            }}
                                                            disabled={isReadOnlyForm}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {!isReadOnlyForm && (
                                                            <i className="fa-solid fa-trash-can" style={{ color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }} onClick={() => handleRemoveMember(i)}></i>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Tệp tin minh chứng *</label>

                            <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                * Chú ý: Hệ thống chỉ nhận tệp minh chứng (PDF, JPG, PNG). Dung lượng tối đa 5MB / tệp
                            </p>

                            {(existingFiles.length > 0 || pendingFiles.length > 0) && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '500px', margin: 0, border: '1px solid #eee' }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingFiles.map((file, idx) => (
                                                <tr key={`ext-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        <span style={{ fontSize: '13px', color: '#666' }}>{file.description || 'Không có mô tả'}</span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            onClick={() => downloadIndividualFile(file)}
                                                            style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }}
                                                            title="Nhấn để tải về"
                                                        >
                                                            {file.name}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {!isReadOnlyForm && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeExistingFile(file.fileId || file.url || file.link || file.data)}
                                                                style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {pendingFiles.map((file, idx) => (
                                                <tr key={`pend-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{existingFiles.length + idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => {
                                                                const newFiles = [...pendingFiles];
                                                                newFiles[idx].description = e.target.value;
                                                                setPendingFiles(newFiles);
                                                            }}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            disabled={isReadOnlyForm}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>
                                                            {file.name} <i className="fa-solid fa-cloud-arrow-up"></i> (Mới)
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {!isReadOnlyForm && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removePendingFile(idx)}
                                                                style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isReadOnlyForm && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />

                                    {(existingFiles.length > 0 || pendingFiles.length > 0) ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>
                                                Đã chọn {existingFiles.length + pendingFiles.length} tệp minh chứng
                                            </p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>
                                                Thêm tệp tin khác
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>
                                                Chưa có tệp nào được chọn
                                            </p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>
                                                Chọn tệp từ thiết bị
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </form>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    {isReadOnlyForm ? (
                        <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                    ) : (
                        <>
                            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmittingForm}>Hủy</button>
                            <button type="submit" form="projectForm" className="btn-submit" disabled={isSubmittingForm}>
                                {isSubmittingForm ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang lưu...</> : "Hoàn tất"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScientificProjectForm;